import React, { useEffect, useRef, useState } from 'react';
import Popup from '../../../../../../../components/general/Popup.tsx';
import { Input } from '../../../../../../../components/general/Input.tsx';
import MoneyInput from '../../../../../../../components/general/MoneyInput/index.ts';
import Form, { type FormInstance } from '../../../../../../../components/form-library/index.ts';
import { useNotifications } from '../../../../../../../lib/NotificationContext.tsx';
import type { GlAccount } from '../../../../../../../hooks/useGLAccounts.ts';
import { GLASelector, GlaFilterPreset } from '../../../../../../../components/specific/GLASelector';
import { SubledgerSelector } from '../../../../../../../components/specific/SubledgerSelector';
import { useSubledgerContext } from '../../../../../../../contexts/SubledgerContext.tsx';
import type { FixedAssetFormValues } from './fixedAssetForm.ts';

const EMPTY_FORM: FixedAssetFormValues = {
    name: '',
    purchase_date: '',
    economic_lifetime_months: undefined,
    purchase_money: null,
    residual_money: null,
    balance_sheet_gl_account: null,
    profit_and_loss_gl_account: null,
    subledger: null,
};

function FixedAssetGlAccountField({
    name,
    label,
    form,
    accounts,
    filterPreset,
    disabled,
    requiredMessage,
}: {
    name: keyof FixedAssetFormValues;
    label: string;
    form: FormInstance<FixedAssetFormValues>;
    accounts: GlAccount[];
    filterPreset: GlaFilterPreset;
    disabled: boolean;
    requiredMessage: string;
}) {
    return (
        <Form.Item name={name} label={label} rules={[{ required: true, message: requiredMessage }]}>
            {({ value }) => (
                <GLASelector
                    accounts={accounts}
                    value={value ?? null}
                    onChange={(v) => form.setFieldValue(name, v)}
                    filterPreset={filterPreset}
                    disabled={disabled}
                />
            )}
        </Form.Item>
    );
}

interface CreateFixedAssetModalProps {
    open: boolean;
    onSubmit: (values: FixedAssetFormValues) => Promise<void>;
    onClose: () => void;
    glAccountOptions: GlAccount[];
    glAccountsLoading: boolean;
    initialValues?: Partial<FixedAssetFormValues>;
}

export default function CreateFixedAssetModal({
    open,
    onSubmit,
    onClose,
    glAccountOptions,
    glAccountsLoading,
    initialValues,
}: CreateFixedAssetModalProps) {
    const { subledgers, createSubledger, canUseSubledgers } = useSubledgerContext();
    const [form] = Form.useForm<FixedAssetFormValues>();
    const [isLoading, setIsLoading] = useState(false);
    const initialValuesRef = useRef(initialValues);
    useEffect(() => {
        initialValuesRef.current = initialValues;
    }, [initialValues]);

    async function handleCreateSubledger(name: string) {
        const created = await createSubledger(name);
        form.setFieldValue('subledger', created);
    }

    const glDropdownDisabled = glAccountsLoading || glAccountOptions.length === 0;

    useEffect(() => {
        setIsLoading(false);
        if (!open) {
            form.setFieldsValue({ ...EMPTY_FORM });
            return;
        }
        form.resetFields();
        if (initialValuesRef.current) {
            form.setFieldsValue({ ...EMPTY_FORM, ...initialValuesRef.current });
        }
    }, [open, form]);

    const handleFinish = async (values: FixedAssetFormValues) => {
        setIsLoading(true);
        try {
            await onSubmit(values);
            form.resetFields();
            onClose();
        } catch {
            return;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Popup
            open={open}
            onClose={onClose}
            onConfirm={() => form.submit()}
            title="Vaste activa toevoegen"
            confirmButtonDisplayLabel="Opslaan"
            confirmButtonDisabled={isLoading}
            Icon={null}
        >
            <Form
                form={form}
                className="py-2"
                initialValues={EMPTY_FORM}
                onFinish={(v) => void handleFinish(v)}
            >
                <Form.Item
                    name="name"
                    label="Omschrijving"
                    rules={[{ required: true, message: 'Vul een omschrijving in.' }]}
                >
                    <Input type="text" autoComplete="off" placeholder="Bijv. Laptop James" />
                </Form.Item>

                <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                        name="purchase_date"
                        label="Aanschafdatum"
                        rules={[{ required: true, message: 'Kies een aanschafdatum.' }]}
                    >
                        <Input type="date" />
                    </Form.Item>

                    <Form.Item
                        name="economic_lifetime_months"
                        label="Afschrijven in maanden"
                        rules={[{ required: true, message: 'Vul het aantal maanden in.' }]}
                    >
                        <Input type="number" min={1} step={1} inputMode="numeric" />
                    </Form.Item>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                        name="purchase_money"
                        label="Aanschafwaarde"
                        rules={[{ required: true, message: 'Vul een aanschafwaarde in.' }]}
                    >
                        <MoneyInput locale="nl-NL" currency="EUR" />
                    </Form.Item>

                    <Form.Item name="residual_money" label="Restwaarde">
                        <MoneyInput locale="nl-NL" currency="EUR" />
                    </Form.Item>
                </div>

                <FixedAssetGlAccountField
                    name="balance_sheet_gl_account"
                    label="Rekening activa"
                    form={form}
                    accounts={glAccountOptions}
                    filterPreset={GlaFilterPreset.FIXED_ASSETS_BS}
                    disabled={glDropdownDisabled}
                    requiredMessage="Selecteer een rekening voor activa."
                />

                <FixedAssetGlAccountField
                    name="profit_and_loss_gl_account"
                    label="Rekening afschrijvingen"
                    form={form}
                    accounts={glAccountOptions}
                    filterPreset={GlaFilterPreset.FIXED_ASSETS_PL}
                    disabled={glDropdownDisabled}
                    requiredMessage="Selecteer een rekening voor afschrijvingen."
                />

                {canUseSubledgers && (
                    <Form.Item name="subledger" noStyle>
                        {({ value }) => (
                            <SubledgerSelector
                                subledgers={subledgers}
                                value={value ?? null}
                                onChange={(subledger) => form.setFieldValue('subledger', subledger)}
                                onCreate={handleCreateSubledger}
                            />
                        )}
                    </Form.Item>
                )}
            </Form>
        </Popup>
    );
}
